import React, {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { useInView } from "react-intersection-observer";

interface LazyLoadProps {
  children: ReactNode;
  height?: number | string;
  offset?: number | string;
  once?: boolean;
  placeholder?: ReactNode;
  className?: string;
}

const parseOffset = (offset: number | string): number => {
  if (typeof offset === "number") {
    return offset;
  }
  const parsed = parseInt(offset, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isWithinViewport = (node: HTMLElement, offsetPx: number): boolean => {
  const rect = node.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.bottom >= -offsetPx &&
    rect.right >= -offsetPx &&
    rect.top <= viewportHeight + offsetPx &&
    rect.left <= viewportWidth + offsetPx
  );
};

/**
 * Behavior:
 * - Content already inside the viewport (plus `offset`) is detected synchronously
 *   before paint and renders its real, interactive children on the first frame.
 * - Off-screen content renders the placeholder and is swapped for the real
 *   children once it scrolls into view (via IntersectionObserver).
 *
 * The synchronous initial check is important: `useInView` reports visibility
 * asynchronously after mount, so without it there is a brief window where a
 * visible card still shows the non-interactive placeholder. A click during that
 * window lands on the placeholder and does nothing, forcing a second click.
 */
const LazyLoad = ({
  children,
  height,
  offset = 150,
  once = true,
  placeholder,
  className
}: LazyLoadProps) => {
  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: once,
    rootMargin: typeof offset === "number" ? `${offset}px` : offset
  });
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [initiallyVisible, setInitiallyVisible] = useState(false);

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node;
      inViewRef(node);
    },
    [inViewRef]
  );

  useLayoutEffect(() => {
    if (initiallyVisible || !nodeRef.current) {
      return;
    }
    if (isWithinViewport(nodeRef.current, parseOffset(offset))) {
      setInitiallyVisible(true);
    }
  }, [initiallyVisible, offset]);

  const heightStyle =
    height === undefined
      ? {}
      : { minHeight: typeof height === "number" ? `${height}px` : height };

  return (
    <div ref={setRefs} style={heightStyle} className={className}>
      {inView || initiallyVisible ? children : placeholder}
    </div>
  );
};

export default LazyLoad;
